/**
 * MobileLayout Component
 * 
 * Full-screen layout for mobile editing.
 * Features:
 * - Full-screen view (not a modal)
 * - Navigation bar with Back and Save buttons
 * - Full form fields
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { EditableItem } from '../types';
import { ItemForm } from '../ItemForm';

export interface MobileLayoutProps {
  item: EditableItem;
  onChange: (patch: Partial<EditableItem>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  token?: string;
  saving?: boolean;
  saveDisabled?: boolean;
}

export const MobileLayout: React.FC<MobileLayoutProps> = ({
  item,
  onChange,
  onSave,
  onClose,
  token,
  saving = false,
  saveDisabled = false,
}) => {
  const mobileContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-text-inverse)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10000,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Navigation Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-primary)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Left: Cancel button */}
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'transparent',
            color: 'var(--color-text-secondary)',
            fontSize: '16px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          Cancel
        </button>

        {/* Center: Item ID */}
        <div
          style={{
            fontSize: '16px',
            color: 'var(--color-text-secondary)',
            fontWeight: 600,
          }}
        >
          #{item.id}
        </div>

        {/* Right: Save button */}
        <button
          onClick={onSave}
          disabled={saving || saveDisabled || !item.name.trim()}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: 'var(--color-accent-secondary)',
            color: 'var(--color-text-inverse)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: saving || saveDisabled || !item.name.trim() ? 'not-allowed' : 'pointer',
            opacity: saving || saveDisabled || !item.name.trim() ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Scrollable Content — extra bottom padding so keyboard doesn't cover lower fields */}
      <div style={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', padding: '16px', paddingBottom: '40vh' }}>
        {/* Form */}
        <ItemForm
          item={item}
          onChange={onChange}
          token={token}
          mode="full"
        />

        {/* Timestamps at bottom - on same line with icons */}
        {(item.created_at || item.updated_at || item.purchased_at) && (
          <div
            style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid var(--color-border)',
              fontSize: '13px',
              color: 'var(--color-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {item.created_at && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
                {new Date(item.created_at).toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: '2-digit',
                })}
              </span>
            )}
            {item.created_at && (item.updated_at || item.purchased_at) && <span>•</span>}
            {item.updated_at && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                </svg>
                {new Date(item.updated_at).toLocaleString(undefined, {
                  month: 'numeric',
                  day: 'numeric',
                  year: '2-digit',
                })}
              </span>
            )}
            {item.updated_at && item.purchased_at && <span>•</span>}
            {item.purchased_at && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {(() => {
                  const dateStr = (item as any).purchased_at;
                  if (dateStr.includes('T')) {
                    return new Date(dateStr).toLocaleString(undefined, { 
                      month: 'numeric', 
                      day: 'numeric', 
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });
                  } else {
                    const [year, month, day] = dateStr.split('-');
                    return `${parseInt(month)}/${parseInt(day)}/${year.slice(2)}`;
                  }
                })()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(mobileContent, document.body);
};

export default MobileLayout;
